import React, { useCallback, useMemo } from "react";
import { CategoryTheme, EMPTY_ARRAY } from "../misc";
import { getTraceColors } from "../../themes";
import { prefixValue, suffixValue, Unit } from "../processors";
import { DataPoint, DataPointWithGraphic } from "./draw";
import { formatNumber } from "../../utils";
import { HorizontalTwoLineLabel } from "./HorizontalTwoLineLabel";

import _ from "lodash";
import ReactDOM from "react-dom";
import styles from "./HorizontalTwoLineLabelWithData.module.scss";
export interface HorizontalTwoLineLabelWithDataProps {
    dataPoints: DataPoint[];
    unit: Unit;
    labels?: string[];
    series?: string[];
    theme?: CategoryTheme;
    maxValue?: number;
    queryHeightWatchingLabels?: () => { watching: HTMLElement[]; update: HTMLElement[] };
    queryWidthWatchingLabels?: () => { watching: HTMLElement[]; update: HTMLElement[] };
    onHover?: (data: DataPointWithGraphic, tooltip: HTMLElement) => void;
}

export const HorizontalTwoLineLabelWithData: React.FC<HorizontalTwoLineLabelWithDataProps> = ({
    dataPoints,
    unit,
    labels,
    series,
    theme,
    maxValue,
    onHover,
    queryHeightWatchingLabels,
    queryWidthWatchingLabels
}) => {
    const _labels = useMemo(() => {
        return (
            labels ??
            _.uniq(
                dataPoints?.map(d => d.label)?.filter(d => d != null && d.trim() !== "") ?? (EMPTY_ARRAY as string[])
            )
        );
    }, [labels, dataPoints]);

    const _series = useMemo(() => {
        return (
            series ??
            _.uniq(
                dataPoints?.map(d => d.series)?.filter(d => d != null && d.trim() !== "") ?? (EMPTY_ARRAY as string[])
            )
        );
    }, [series, dataPoints]);

    const seriesBackground = useMemo(() => {
        return getTraceColors(theme, _series?.length, _series);
    }, [theme, _series]);

    const _onHover = useCallback(
        (data: DataPointWithGraphic, tooltip: HTMLElement) => {
            if (onHover) {
                onHover(data, tooltip);
                return;
            }

            const seriIndex = _series.findIndex(seri => seri === data.series);
            const background = seriesBackground?.[seriIndex] ?? "black";

            ReactDOM.render(
                <div className={styles.tooltip}>
                    <div>
                        <span
                            style={{
                                background
                            }}
                            className={`${styles.colorBox}`}
                        ></span>
                        <span>{data.series}</span>
                    </div>
                    <div>
                        {data.label}: {prefixValue(data.value, unit)} {data.value} {suffixValue(data.value, unit)}
                    </div>
                </div>,
                tooltip
            );
        },
        [unit, _series, seriesBackground, onHover]
    );

    const displayedLabels = useMemo(() => {
        const labelToGroups = _.groupBy(dataPoints, d => d.label);
        const labelValues = _labels.map(label => {
            return _.sum(labelToGroups[label]?.map(d => d.value ?? 0));
        });

        return _labels.map((label, index) => {
            return (
                <>
                    <div>{label}</div>
                    <div>
                        {prefixValue(0, unit)} {formatNumber(labelValues?.[index])} {suffixValue(0, unit)}
                    </div>
                </>
            );
        });
    }, [unit, _labels, dataPoints]);

    return (
        <HorizontalTwoLineLabel
            dataPoints={dataPoints}
            displayedLabels={displayedLabels}
            labels={_labels}
            series={_series}
            seriesBackground={seriesBackground}
            maxValue={maxValue}
            onHover={_onHover}
            queryHeightWatchingLabels={queryHeightWatchingLabels}
            queryWidthWatchingLabels={queryWidthWatchingLabels}
        />
    );
};
